import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import RETAILERS_KEYS from 'api/retailers/retailers.keys';
import { applyMovePayload } from 'utils/reorder';
import PRODUCTS_KEYS from '../products.keys';
import PRODUCTS_MUTATIONS from '../products.mutation';
import PRODUCTS_QUERIES from '../products.queries';

export const useFetchFavoriteProducts = () =>
  useQuery({
    queryKey: PRODUCTS_KEYS.FAVORITE_PRODUCTS(),
    queryFn: () => PRODUCTS_QUERIES.FETCH_FAVORITE_PRODUCTS(),
    select: (data) =>
      data.filter((favoriteProduct) => favoriteProduct.product.child_products.length > 0),
    staleTime: 1 * 60 * 1000, // 1 minute
  });

export const useAddFavoriteProduct = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: PRODUCTS_MUTATIONS.ADD_FAVORITE_PRODUCT,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: PRODUCTS_KEYS.FAVORITE_PRODUCTS() });
    },
  });
};

export const useDeleteFavoriteProduct = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: PRODUCTS_MUTATIONS.DELETE_FAVORITE_PRODUCT,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: PRODUCTS_KEYS.FAVORITE_PRODUCTS() });
      queryClient.invalidateQueries({ queryKey: RETAILERS_KEYS.RETAILERS_FAVORITE_PRODUCTS() });
    },
  });
};

export const useDeleteFavoriteProducts = () => {
  const queryClient = useQueryClient();
  const queryKey = PRODUCTS_KEYS.FAVORITE_PRODUCTS();

  return useMutation({
    mutationFn: PRODUCTS_MUTATIONS.DELETE_FAVORITE_PRODUCTS,
    onMutate: (uids) => {
      queryClient.cancelQueries({ queryKey });

      const previousFavoriteProducts = queryClient.getQueryData(queryKey);

      queryClient.setQueryData(queryKey, (current) => {
        if (!current) return current;
        return current.filter((favoriteProduct) => !uids.includes(favoriteProduct.uid));
      });

      return { previousFavoriteProducts };
    },
    onError: (_error, _uids, context) => {
      if (context?.previousFavoriteProducts) {
        queryClient.setQueryData(queryKey, context.previousFavoriteProducts);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey });
    },
  });
};

export const useReorderFavoriteProducts = () => {
  const queryClient = useQueryClient();
  const queryKey = PRODUCTS_KEYS.FAVORITE_PRODUCTS();

  return useMutation({
    mutationFn: PRODUCTS_MUTATIONS.MOVE_FAVORITE_PRODUCT,
    onMutate: (move) => {
      queryClient.cancelQueries({ queryKey });

      const previousFavoriteProducts = queryClient.getQueryData(queryKey);

      queryClient.setQueryData(queryKey, (current) => {
        if (!current) return current;
        return applyMovePayload(current, move);
      });

      return { previousFavoriteProducts };
    },
    onError: (_error, _move, context) => {
      if (context?.previousFavoriteProducts) {
        queryClient.setQueryData(queryKey, context.previousFavoriteProducts);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey });
    },
  });
};
